import {
    CustomUpgradeValidationFieldsFragment,
    In_Queue_Model_Variants_Enum as ModelVariantEnum,
    SubscribeToCustomUpgradesDocument,
    SubscribeToCustomUpgradesSubscription,
    SubscribeToCustomUpgradesSubscriptionVariables,
    useGetCustomUpgradesQuery
} from "generated/graphql";
import { LOADING } from "graphql/helpers/queryHelpers";
import { useCustomUpgradesStatus } from "in_queue/hooks/useCustomUpgradesStatus";
import {
    dedupeValidationFailures,
    getModelSetValidationInfo,
    getRelevantModelSet,
    ModelSetValidation,
    ModelSetValidationInfo
} from "in_queue/utils/customUpgradeValidation";
import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo
} from "react";
import { Loading } from "types/loadingType";
import { useCluster } from "./ClusterContext";
import { useSubscriptionForCluster } from "./ProjectDataContext";

export type ModelVariant = {
    readonly description: string;
    readonly variant: ModelVariantEnum;
};

export type CustomUpgradeFile = {
    readonly id: string;
    readonly sourceFileFormat: string;
    readonly sourceFileName: string;
    readonly variant: ModelVariant | null;

    readonly createdAt: Date;
    readonly updatedAt: Date;
};

const CUSTOM_UPGRADE_TYPE_NIRA_SHARED = "nira_shared";

export type CustomUpgrade = {
    readonly id: string;
    readonly clusterId: string;
    /** Published by Nira to every team with the add-on. Customers can't archive or validate it. */
    readonly isShared: boolean;
    readonly files: readonly CustomUpgradeFile[];
    // TODO: (phharvie) this should be a Hasura derived field
    readonly isModelSpecific: boolean;
    readonly name: string;
    readonly status: string;
    readonly notes: string | null;
    readonly createdAt: Date;
    readonly modelSetValidationInfo: ModelSetValidationInfo;
};

interface CustomUpgradesContextValue {
    customUpgrades: Loading<CustomUpgrade[]>;
}

const CustomUpgradesContext = createContext<CustomUpgradesContextValue>({
    customUpgrades: "loading"
});

// Validations resolve asynchronously on the backend (REQUESTED → RUNNING →
// SUCCEEDED/FAILED); a live subscription pushes each transition so the UI
// updates without a reload. The rows come from a query the subscription feeds,
// rather than from the subscription alone, so requesting a validation can
// refetch the row it just created instead of leaving the UI on the previous
// state until Hasura's next push.
const useCustomUpgradesWithValidations = (
    clusterId: string,
    subscriptionId: string | null,
    skip: boolean
) => {
    const { data, loading, error, subscribeToMore } = useGetCustomUpgradesQuery(
        {
            variables: { clusterId, subscriptionId },
            skip
        }
    );

    useEffect(() => {
        if (skip) {
            return undefined;
        }
        return subscribeToMore<
            SubscribeToCustomUpgradesSubscription,
            SubscribeToCustomUpgradesSubscriptionVariables
        >({
            document: SubscribeToCustomUpgradesDocument,
            variables: { clusterId, subscriptionId },
            updateQuery: (previous, { subscriptionData }) =>
                subscriptionData.data
                    ? {
                          ...previous,
                          custom_upgrades: subscriptionData.data.custom_upgrades
                      }
                    : previous
        });
    }, [subscribeToMore, clusterId, subscriptionId, skip]);

    return { data, loading, error };
};

const toModelSetValidation = (
    row: CustomUpgradeValidationFieldsFragment,
    upgradeVariants: ReadonlySet<ModelVariantEnum>,
    failureCodeDescriptions: ReadonlyMap<string, string>
): ModelSetValidation => ({
    clusterModelSetId: row.in_queue_cluster_model_set_id,
    status: row.validation_status,
    errorMessage: row.error_message ?? null,
    submittedAt: row.submitted_at ? new Date(row.submitted_at) : null,
    completedAt: row.completed_at ? new Date(row.completed_at) : null,
    failures: dedupeValidationFailures(
        row.failures.map((failure) => ({
            severity: failure.severity,
            code: failure.code,
            message: failure.message,
            lineNumber: failure.line_number ?? null,
            offendingText: failure.offending_text ?? null,
            suggestedFix: failure.suggested_fix ?? null,
            warnId: failure.warn_id ?? null,
            variant: failure.variant,
            variantDescription: failure.model_variant.description
        })),
        upgradeVariants,
        failureCodeDescriptions
    )
});

export const CustomUpgradesProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const cluster = useCluster();
    const clusterId = cluster.id;
    const subscription = useSubscriptionForCluster();
    const activeSubscription =
        subscription === LOADING ? undefined : subscription;
    // Only fetch when fully unlocked — there's nothing to show in the `locked`
    // (upsell) or `hidden` states.
    const canFetch = useCustomUpgradesStatus() === "enabled";

    const { data, loading, error } = useCustomUpgradesWithValidations(
        clusterId,
        activeSubscription?.subscriptionId ?? null,
        !canFetch
    );

    const customUpgrades = useMemo<Loading<CustomUpgrade[]>>(() => {
        if (!canFetch) {
            return [];
        }

        if (loading) {
            return "loading";
        }

        if (!data || error) {
            console.error("Failed to load custom upgrades", error);
            return [];
        }

        const failureCodeDescriptions = new Map<string, string>();
        (data.custom_upgrade_validation_failure_codes ?? []).forEach((row) => {
            failureCodeDescriptions.set(row.code, row.description);
        });

        const relevantModelSet = getRelevantModelSet(
            cluster,
            activeSubscription
        );

        return (data.custom_upgrades ?? []).map((upgrade): CustomUpgrade => {
            const files = upgrade.custom_upgrade_files.map((file) => ({
                id: file.id,
                sourceFileFormat: file.source_file_format,
                sourceFileName: file.source_file_name,
                variant:
                    file.model_variant && file.variant // Necessary for enum typing and description
                        ? {
                              description: file.model_variant.description,
                              variant: file.variant
                          }
                        : null,
                createdAt: new Date(file.created_at),
                updatedAt: new Date(file.updated_at)
            }));

            const isModelSpecific = upgrade.custom_upgrade_files.some(
                (file) => file.model_variant !== null
            );

            const upgradeVariants = new Set(
                files
                    .map((file) => file.variant?.variant)
                    .filter((variant): variant is ModelVariantEnum =>
                        Boolean(variant)
                    )
            );

            const toValidation = (row: CustomUpgradeValidationFieldsFragment) =>
                toModelSetValidation(
                    row,
                    upgradeVariants,
                    failureCodeDescriptions
                );
            const currentRow = upgrade.current_validation?.[0];

            return {
                id: upgrade.id,
                clusterId: upgrade.cluster_id,
                isShared: upgrade.type === CUSTOM_UPGRADE_TYPE_NIRA_SHARED,
                files: files,
                isModelSpecific: isModelSpecific,
                name: upgrade.name,
                status: upgrade.status,
                notes: upgrade.notes ?? null,
                createdAt: new Date(upgrade.created_at),
                modelSetValidationInfo: getModelSetValidationInfo(
                    upgrade.model_set_validations.map(toValidation),
                    currentRow ? toValidation(currentRow) : null,
                    relevantModelSet
                )
            };
        });
    }, [canFetch, data, loading, error, cluster, activeSubscription]);

    return (
        <CustomUpgradesContext.Provider value={{ customUpgrades }}>
            {children}
        </CustomUpgradesContext.Provider>
    );
};

export const useCustomUpgrades = (): Loading<CustomUpgrade[]> => {
    const context = useContext(CustomUpgradesContext);
    return context.customUpgrades;
};
